/**
 *  String transformation helpers. Can be imported in any component.
 */
import { isArray, isObject } from "./typeValidations";

export const strToCamelCase = (str: string) => {
    /**
     * Converts a string from snake_case or kebab-case to camelCase while removing underscores or hyphens
     * before numbers.
     *
     * @param {string} str - The input string in snake_case or kebab-case format.
     * @returns {string} - The converted string in camelCase format.
     */
    return str
        .replace(/[_-]([a-z])/g, (_, match) => match.toUpperCase())
        .replace(/[_-]/g, "");
};

export const strToSnakeCase = (str: string) => {
    /**
     * Uses regular expressions to convert a string's casing (accepts either camel, pascal or kebab case)
     * to snake case. It adds underscore before numbers at the end of the string
     * @param  {[string]} str
     * @return {[string]}
     */
    str = str
        .replace(/([a-z])([A-Z])/g, "$1_$2") // Handle camelCase and PascalCase
        .replace(/-/g, "_"); // Handle kebab-case

    str = str.replace(/(\d+$)/, "_$1"); // Add underscore before numbers at the end of the string

    return str.toLowerCase();
};

export const keysToCamelCase = (obj: any) => {
    /**
     * Tests if the function param is an object. If yes, it loops through all of
     * its keys, converts each key to camel case, and recursively call the
     * keysToCamel function on the value of each item in the object for nested objects.
     * @param  {[any]} obj
     * @return {[Object]}
     */
    if (isObject(obj)) {
        const n = {};

        Object.keys(obj).forEach((key) => {
            n[strToCamelCase(key)] = keysToCamelCase(obj[key]);
        });

        return n;
    } else if (isArray(obj)) {
        return obj.map((i) => {
            return keysToCamelCase(i);
        });
    }

    return obj;
};

export const keysToSnakeCase = (obj: any) => {
    /**
     * Tests if the function param is an object. If yes, it loops through all of
     * its keys, converts each key to snake case, and recursively call the
     * keysSnakeCase function on the value of each item in the object for nested objects.
     * @param  {[any]} obj
     * @return {[Object]}
     */
    if (isObject(obj)) {
        const n = {};

        Object.keys(obj).forEach((key) => {
            n[strToSnakeCase(key)] = keysToSnakeCase(obj[key]);
        });

        return n;
    } else if (isArray(obj)) {
        return obj.map((i) => {
            return keysToSnakeCase(i);
        });
    }

    return obj;
};

export const camelCaseToRegularText = (str: string) => {
    /**
     * This function takes a string in camelCase or PascalCase and converts it into regular text by
     * inserting spaces between words wherever an uppercase letter follows a lowercase letter.
     * @param {[string]} str
     * @returns {[string]}
     */
    return str.replace(/([a-z])([A-Z])/g, "$1 $2");
};

export const kebabCaseToRegularText = (str: string) => {
    /**
     * This function takes a string in kebab-case and converts it into regular text by
     * inserting spaces between words wherever an uppercase letter follows a lowercase letter.
     * @param {[string]} str
     * @returns {[string]}
     */
    const words = str.split("-");

    // Capitalize the first letter of each word and join them with a space
    const spacedText = words
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    return spacedText;
};

/**
 * Extracts the shortcode from a given absolute URL (e.g. window.location.href).
 * The shortcode is the first path segment after the origin — index [3] when the
 * URL is split by "/", since indices 0-2 are the protocol, empty string, and host.
 * Returns an empty string if there is no such segment.
 * Query parameters on that segment are stripped before returning.
 * @param {string} currentURL
 * @returns {string}
 */
export const extractShortcodeFromUrl = (currentURL: string) => {
    const urlParts = currentURL.split("/");

    if (urlParts.length > 3) {
        const shortcode = urlParts[3].split("?")[0];
        return shortcode;
    } else {
        return "";
    }
};

/**
 * Concatenates any number of given strings with the given separator.
 * It trims and filters out empty, null, and undefined strings.
 * @param {string} separator
 * @param {...(string | null | undefined)} strings - strings to concatenate
 * @returns {string}
 */
export const concatenateStrings = (
    separator: string,
    ...strings: (string | null | undefined)[]
): string => {
    const filteredStrings = strings
        .map((s) => (s != null ? s.trim() : ""))
        .filter((s) => s !== "");

    const result = filteredStrings.join(separator);

    return result;
};

export const capitaliseFirstLetterOfFirstWord = (str) => {
    /**
     * Capitalises the first letter of the first word in a given string.
     * The rest of the string remains unchanged.
     * @param {string} str - The input string.
     * @returns {string} - The modified string with the first letter of the first word capitalized.
     */
    if (!str) return str;
    str = str.toLowerCase();

    return str.charAt(0).toUpperCase() + str.slice(1);
};

export const capitaliseFirstLetterOfEachWord = (str) => {
    /**
     * Capitalises the first letter of each word in a given string.
     * The rest of the string remains unchanged.
     * @param {string} str - The input string.
     * @returns {string} - The modified string with the first letter of each word capitalized.
     */
    if (!str) return str;
    str = str.toLowerCase();

    return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const copyToClipboard = async (text: string | undefined) => {
    /**
     * Copies the given text to the clipboard.
     * @param {string | undefined} text
     * @returns {Promise<void>} a promise that resolves once the text is copied
     */
    await navigator.clipboard.writeText(String(text));
};

/**
 * Compares two string arrays and returns true if they have the same contents,
 * regardless of the order of elements.
 * @param {string[]} a - The first string array.
 * @param {string[]} b - The second string array.
 * @returns {boolean} - True if the arrays have the same contents, false otherwise.
 */
export const stringArraysHaveSameContents = (a: string[], b: string[]) => {
    if (a.length !== b.length) {
        return false;
    }

    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    return sortedA.every((value, index) => value === sortedB[index]);
};
